/**
 * Statement Service
 * Handles statement generation API calls
 */

import apiClient from '@/api/client';
import { API_ENDPOINTS } from '@/config/api.config';

/**
 * Response from the generate statement endpoint
 */
interface GenerateStatementResponse {
  downloadToken: string;
  downloadUrl: string;
  generatedAt: string;
  fileSizeBytes: number;
  periodEndDate: string;
}

/**
 * Generate statement and get download token
 * Uses default end date (end of last month) when no date is specified
 */
export const requestStatementGeneration = async (): Promise<GenerateStatementResponse> => {
  const response = await apiClient.post<GenerateStatementResponse>(
    API_ENDPOINTS.STATEMENTS.GENERATE,
    {} // Empty request body - backend will use default end date
  );

  return response.data;
};

/**
 * Download statement PDF by token
 */
export const downloadStatementByToken = async (token: string): Promise<Blob> => {
  const response = await apiClient.get<Blob>(
    API_ENDPOINTS.STATEMENTS.DOWNLOAD(token),
    {
      responseType: 'blob',
    }
  );

  return response.data;
};

/**
 * Generate and download statement PDF (two-step process)
 * 1. POST to generate statement and get download token
 * 2. GET to download the PDF using the token
 */
export const generateStatement = async (): Promise<Blob> => {
  // Step 1: Generate statement and get download token
  const generateResponse = await requestStatementGeneration();

  // Step 2: Download the PDF using the token
  const pdfBlob = await downloadStatementByToken(generateResponse.downloadToken);

  return pdfBlob;
};

/**
 * Open statement in new window
 */
export const openStatementInNewWindow = async (): Promise<void> => {
  const blob = await generateStatement();
  const url = window.URL.createObjectURL(blob);
  window.open(url, '_blank');

  // Clean up after a delay
  setTimeout(() => {
    window.URL.revokeObjectURL(url);
  }, 100);
};
